import { useMutation, useQuery } from "@apollo/client";
import { ReactNode, useCallback, useEffect, useRef } from "react";

import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { contactUsCallback } from "@helpers/helpers";

import {
  GetAvailableMergeIntegrationsQuery,
  GetMergeDevIntegrationsQuery,
  GetMergeDevIntegrationsQueryVariables,
} from "../../../../types/graphql-schema";
import createMergeLinkTokenMutation from "../../graphql/create-merge-link-token";
import getAvailableMergeIntegrationsQuery from "../../graphql/get-available-merge-integrations";
import getMergeDevIntegrationsQuery from "../../graphql/get-merge-dev-integrations";
import relinkMergeDevIntegrationMutation from "../../graphql/relink-merge-dev-integration";
import removeMergeDevIntegrationMutation from "../../graphql/remove-merge-dev-integration";
import IntegrationDetailLayout from "./integration-detail-layout";
import {
  MERGE_CATEGORY_DESCRIPTIONS,
  getIntegrationCategoryLabel,
  isVisibleIntegrationCategory,
  normalizeIntegrationCategory,
  requiresManualSetupIntegration,
} from "./integrations-constants";
import { useMergeIntegrationLink } from "./use-merge-integration-link";

const MergeIntegrationDetailPage = ({
  organization,
  slug,
  open,
  onClose,
}: {
  organization: any;
  slug: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { data: connectedData, refetch: refetchConnected } = useQuery<
    GetMergeDevIntegrationsQuery,
    GetMergeDevIntegrationsQueryVariables
  >(getMergeDevIntegrationsQuery, {
    variables: { organizationId: organization.id },
  });
  const { data: availableData } = useQuery<GetAvailableMergeIntegrationsQuery>(
    getAvailableMergeIntegrationsQuery,
    { skip: !waffle.flag_is_active("merge_dev_enabled") },
  );

  const connectedAll = (connectedData?.mergeDevIntegrations ?? []).filter(
    (i): i is NonNullable<typeof i> => i !== null,
  );
  const connected = connectedAll.find((i) => i.integrationName === slug);
  const available = (availableData?.availableMergeIntegrations ?? [])
    .filter((i): i is NonNullable<typeof i> => i !== null)
    .find((i) => i.slug === slug);

  const isConnected = Boolean(connected);
  const name =
    connected?.integrationDisplayName ||
    connected?.integrationName ||
    available?.name ||
    slug;
  // Some Merge integrations require a setup step from Topicflow before they can
  // be connected. For those we skip Merge's connect flow and point the user to
  // support instead.
  const needsManualSetup = !isConnected && requiresManualSetupIntegration(name);
  const logo =
    connected?.iconUrl ||
    connected?.fullImageUrl ||
    available?.squareImage ||
    available?.image ||
    "";
  const category =
    connected?.category ||
    available?.categories
      .filter((c): c is string => c !== null)
      .find(isVisibleIntegrationCategory) ||
    "";
  const categoryLabel = category ? getIntegrationCategoryLabel(category) : "";
  const categoryDescription =
    MERGE_CATEGORY_DESCRIPTIONS[normalizeIntegrationCategory(category)] || "";

  const [createMergeLinkToken] = useMutation(createMergeLinkTokenMutation);
  const [removeMergeDevIntegration, { loading: isDisconnecting }] = useMutation(
    removeMergeDevIntegrationMutation,
    {
      onCompleted: () => {
        hasDisconnectedRef.current = true;
        successNotificationVar({ title: `${name} disconnected` });
        refetchConnected();
        onClose();
      },
    },
  );
  const [relinkMergeDevIntegration, { loading: isRelinking }] = useMutation(
    relinkMergeDevIntegrationMutation,
  );

  // Track auto-flow state across renders.
  // - linkStartedAutomaticallyRef: did we (vs. the user) trigger the link?
  // - justConnectedRef: did the exchange just succeed? (set in onSuccess)
  // - wasLinkingRef: previous render's isLinking, for transition detection
  const linkStartedAutomaticallyRef = useRef(false);
  const justConnectedRef = useRef(false);
  const wasLinkingRef = useRef(false);
  // Set once the user disconnects from within this dialog so we keep showing the
  // "Not connected" state instead of auto-relaunching Merge's connect flow.
  const hasDisconnectedRef = useRef(false);

  const { startLink, isLinking } = useMergeIntegrationLink({
    onSuccess: () => {
      justConnectedRef.current = true;
      refetchConnected();
    },
    successMessage: isConnected
      ? "Integration re-linked successfully"
      : "Integration connected successfully",
  });

  const handleConnect = useCallback(async () => {
    if (requiresManualSetupIntegration(name)) {
      return;
    }
    if (!category) {
      errorNotificationVar({
        title: "Cannot connect",
        description: "No category available for this integration.",
      });
      return;
    }
    try {
      const result = await createMergeLinkToken({
        variables: { integrationSlug: slug, category },
      });
      if (result.data?.createMergeLinkToken?.success) {
        const linkToken = result.data.createMergeLinkToken.linkToken;
        const originId = result.data.createMergeLinkToken.endUserOriginId;
        if (linkToken && originId) {
          startLink(linkToken, originId);
        }
      } else {
        errorNotificationVar({
          title: "Error",
          description:
            result.data?.createMergeLinkToken?.error ||
            "Failed to create link token",
        });
      }
    } catch (error) {
      errorNotificationVar({
        title: "Error",
        description: `${error}`,
      });
    }
  }, [name, category, slug, createMergeLinkToken, startLink]);

  const handleRelink = useCallback(async () => {
    if (!connected) {
      return;
    }
    try {
      const result = await relinkMergeDevIntegration({
        variables: { integrationId: connected.id },
      });
      if (result.data?.relinkMergeDevIntegration?.success) {
        const linkToken = result.data.relinkMergeDevIntegration.linkToken;
        const originId = result.data.relinkMergeDevIntegration.endUserOriginId;
        if (linkToken && originId) {
          startLink(linkToken, originId);
        }
      } else {
        errorNotificationVar({
          title: "Error",
          description:
            result.data?.relinkMergeDevIntegration?.error ||
            "Failed to create re-link token",
        });
      }
    } catch (error) {
      errorNotificationVar({
        title: "Error",
        description: `Failed to create re-link token. ${error}`,
      });
    }
  }, [connected, relinkMergeDevIntegration, startLink]);

  // For not-yet-connected integrations, jump straight to Merge's modal instead
  // of showing our detail dialog first. The detail dialog will appear after a
  // successful connection because `isConnected` flips true.
  useEffect(() => {
    if (linkStartedAutomaticallyRef.current) return;
    if (hasDisconnectedRef.current) return; // just disconnected — don't relaunch
    if (!connectedData) return; // wait for connection data
    if (isConnected) return; // already connected — show dialog instead
    if (needsManualSetup) return; // show the contact-us dialog instead
    if (!category) return; // need a category to create a link token
    linkStartedAutomaticallyRef.current = true;
    handleConnect();
  }, [connectedData, isConnected, needsManualSetup, category, handleConnect]);

  // When the link flow ends, close our dialog if the user canceled an
  // auto-started link (no point showing an empty "not connected" dialog).
  useEffect(() => {
    if (wasLinkingRef.current && !isLinking) {
      if (linkStartedAutomaticallyRef.current && !justConnectedRef.current) {
        onClose();
      }
      justConnectedRef.current = false;
      linkStartedAutomaticallyRef.current = false;
    }
    wasLinkingRef.current = isLinking;
  }, [isLinking, onClose]);

  // Merge's CDN script renders its own viewport backdrop. Override it to match
  // our app's modal overlay color (gray-500 / 75% opacity). We track every
  // element we modify so we can restore it on cleanup — otherwise our
  // `!important` declaration outlasts the Merge modal and leaves a gray
  // overlay covering the app.
  useEffect(() => {
    if (!isLinking) return;
    const targetBg = "rgba(107, 114, 128, 0.75)";
    const overridden = new Set<HTMLElement>();
    const isDarkBackdrop = (el: HTMLElement) => {
      const cs = window.getComputedStyle(el);
      if (cs.position !== "fixed" && cs.position !== "absolute") return false;
      const rect = el.getBoundingClientRect();
      if (rect.width < window.innerWidth * 0.85) return false;
      if (rect.height < window.innerHeight * 0.85) return false;
      const bg = cs.backgroundColor;
      if (!bg || bg === "transparent" || bg === "rgba(0, 0, 0, 0)")
        return false;
      const m = bg.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
      if (!m) return false;
      const luminance = (parseInt(m[1]) + parseInt(m[2]) + parseInt(m[3])) / 3;
      return luminance < 100;
    };
    const apply = () => {
      document.body.querySelectorAll<HTMLElement>("*").forEach((el) => {
        const alreadyOurs = overridden.has(el);
        if (!alreadyOurs && !isDarkBackdrop(el)) return;
        // Track it (so cleanup can restore even after we mutate its color).
        overridden.add(el);
        // Re-apply if Merge has clobbered our value.
        if (el.style.getPropertyValue("background-color") !== targetBg) {
          el.style.setProperty("background-color", targetBg, "important");
        }
      });
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    return () => {
      observer.disconnect();
      overridden.forEach((el) => {
        el.style.removeProperty("background-color");
      });
    };
  }, [isLinking]);

  if (connectedData !== undefined && !isConnected && !available) {
    return (
      <IntegrationDetailLayout
        open={open}
        onClose={onClose}
        name="Integration not found"
        logo=""
      >
        <p className="text-sm text-gray-500">
          We couldn’t find an integration with the slug “{slug}”.
        </p>
      </IntegrationDetailLayout>
    );
  }

  // Don't render our dialog while we're not yet connected — Merge's modal is
  // either about to open (auto-fired above) or already open. Once the user
  // successfully connects, isConnected flips true and the dialog appears
  // showing the new "Enabled" state. The exception is right after the user
  // disconnects from within this dialog: we keep it open to reflect the new
  // "Not connected" state rather than vanishing or relaunching Merge.
  if (!isConnected && !hasDisconnectedRef.current && !needsManualSetup) {
    return null;
  }

  const handleDisconnect = () => {
    if (!connected) {
      return;
    }
    removeMergeDevIntegration({ variables: { integrationId: connected.id } });
  };

  const description = needsManualSetup ? (
    <p>
      This integration requires a short setup step from Topicflow before it can
      be connected to your organization. Email{" "}
      <a
        href={`mailto:support@topicflow.com?subject=${encodeURIComponent(
          `Enable ${name} integration`,
        )}`}
        className="text-blue-600 hover:underline"
      >
        support@topicflow.com
      </a>{" "}
      or send us a message and we will help get it enabled.
    </p>
  ) : (
    <p>
      {categoryDescription || `Connect your ${name} account.`}
      {categoryLabel ? ` (${categoryLabel})` : ""}
    </p>
  );

  let actions: ReactNode;
  if (isConnected) {
    actions = (
      <>
        <Button
          theme={buttonTheme.textRed}
          disabled={isDisconnecting}
          onClick={handleDisconnect}
        >
          {isDisconnecting ? "Disconnecting..." : "Disconnect"}
        </Button>
        <Button
          theme={buttonTheme.lightGray}
          disabled={isRelinking}
          onClick={handleRelink}
        >
          {isRelinking ? "Re-linking..." : "Re-link"}
        </Button>
      </>
    );
  } else if (needsManualSetup) {
    actions = (
      <>
        <Button theme={buttonTheme.lightGray} onClick={onClose}>
          Close
        </Button>
        <Button
          theme={buttonTheme.primary}
          onClick={() => {
            contactUsCallback();
            onClose();
          }}
        >
          Message us
        </Button>
      </>
    );
  } else {
    actions = (
      <Button theme={buttonTheme.primary} onClick={handleConnect}>
        Connect
      </Button>
    );
  }

  let statusLabel = "Not connected";
  let statusTone: "enabled" | "neutral" | "warning" = "neutral";
  if (isConnected) {
    statusLabel = "Enabled";
    statusTone = "enabled";
  } else if (needsManualSetup) {
    statusLabel = "Manual setup required";
    statusTone = "warning";
  }

  return (
    <IntegrationDetailLayout
      open={open}
      onClose={onClose}
      name={name}
      logo={logo}
      description={description}
      statusLabel={statusLabel}
      statusTone={statusTone}
      actions={actions}
    />
  );
};

export default MergeIntegrationDetailPage;
